"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Key } from "react";
import { DataGrid, type SortDirection } from "@xefi/x-react/datagrid";
import { useTranslation } from "react-i18next";
import type { NationalTransferModel } from "../../../../domain/models/NationalTransfer";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useSearchPending } from "@/hooks/useSearchPending";
import { useNationalTransfers } from "@/hooks/useNationalTransfers";
import { usePaymentsDateRange, usePaymentsDealerships, usePaymentsSubsidiaryId } from "@/store/usePaymentsFiltersStore";
import { usePaymentsSearchStore, usePaymentsSearchTerm } from "@/store/usePaymentsSearchStore";
import type { MissingCardPaymentState, PaymentsDoneState, PaymentsSortConfig } from "@/types/payments";
import { isForbiddenError } from "@/utils/apiError";
import ExportButton from "../common/ExportButton";
import HistoryDrawer from "../common/HistoryDrawer";
import PaymentsDoneFilter from "../common/PaymentsDoneFilter";
import PaymentsSearchInput from "../common/PaymentsSearchInput";
import { getSharedFiltersSignature, stateToScopes, toDateFilters, toDealershipFilters, toDoneFilters, toSearchFilter, toSubsidiaryScopes } from "../common/paymentsQuery";
import SegmentedControl from "../common/SegmentedControl";
import { type EditableField, NationalTransferRowEditor } from "./NationalTransferRowEditor";
import { useNationalTransfersColumns } from "./nationalTransfersColumns";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 25;
const TRACKED_FIELDS = ["invoice_reference", "to_do", "comment"] as const;
const SEARCH_FIELDS = ["operation_reason", "invoice_reference", "comment", "piece_number", "reference", "bankAccount.dealership", "plaque.label"];
const AMOUNT_SEARCH_FIELDS = ["amount"];
const DATE_FIELD = {
  field: "validation_date",
  kind: "date"
} as const;
export default function NationalTransfersTable() {
  const $ = _c(106);
  const {
    t
  } = useTranslation();
  const subsidiaryId = usePaymentsSubsidiaryId();
  const dateRange = usePaymentsDateRange();
  const dealerships = usePaymentsDealerships();
  const [state, setState] = useState("in_progress");
  const [done, setDone] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sort, setSort] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingField, setEditingField] = useState("invoice_reference");
  const anchorRef = useRef(null);
  let t0;
  if ($[0] !== state || $[1] !== subsidiaryId) {
    t0 = [...stateToScopes(state), ...toSubsidiaryScopes(subsidiaryId)];
    $[0] = state;
    $[1] = subsidiaryId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const scopes = t0;
  const search = usePaymentsSearchTerm("national_transfers");
  const setSearchTerm = usePaymentsSearchStore(_temp);
  const debouncedSearch = useDebouncedValue(search);
  let searchFilter;
  let t1;
  if ($[3] !== dateRange || $[4] !== dealerships || $[5] !== debouncedSearch || $[6] !== done) {
    const base = toDateFilters(dateRange, DATE_FIELD);
    let t2;
    if ($[9] !== debouncedSearch) {
      t2 = toSearchFilter(debouncedSearch, SEARCH_FIELDS, AMOUNT_SEARCH_FIELDS);
      $[9] = debouncedSearch;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    searchFilter = t2;
    t1 = [...base, ...toDoneFilters(done), ...toDealershipFilters(dealerships)];
    $[3] = dateRange;
    $[4] = dealerships;
    $[5] = debouncedSearch;
    $[6] = done;
    $[7] = searchFilter;
    $[8] = t1;
  } else {
    searchFilter = $[7];
    t1 = $[8];
  }
  const scoped = t1;
  let t2;
  if ($[11] !== scoped || $[12] !== searchFilter) {
    t2 = searchFilter ? [...scoped, searchFilter] : scoped;
    $[11] = scoped;
    $[12] = searchFilter;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const filters = t2;
  let t3;
  if ($[14] !== dateRange || $[15] !== subsidiaryId) {
    t3 = getSharedFiltersSignature({
      subsidiaryId,
      domaIds: [],
      dateRange
    });
    $[14] = dateRange;
    $[15] = subsidiaryId;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== dealerships) {
    t4 = dealerships.join(",");
    $[17] = dealerships;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const filtersSignature = `${t3}|${debouncedSearch}|${done}|${t4}`;
  const [previousSignature, setPreviousSignature] = useState(filtersSignature);
  if (filtersSignature !== previousSignature) {
    setPreviousSignature(filtersSignature);
    setPage(1);
    setEditingRowId(null);
  }
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setEditingRowId(null);
    };
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleCloseEditor = t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (row, field, anchor) => {
      anchorRef.current = anchor;
      setEditingField(field);
      setEditingRowId(row.id);
    };
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleOpenEditor = t6;
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      onOpenEditor: handleOpenEditor,
      onOpenHistory: setHistoryRow
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const columns = useNationalTransfersColumns(t7);
  let t8;
  if ($[22] !== sort) {
    t8 = sort ? [sort] : undefined;
    $[22] = sort;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== filters || $[25] !== page || $[26] !== rowsPerPage || $[27] !== scopes || $[28] !== t8) {
    t9 = {
      filters,
      scopes,
      sorts: t8,
      page,
      limit: rowsPerPage
    };
    $[24] = filters;
    $[25] = page;
    $[26] = rowsPerPage;
    $[27] = scopes;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const {
    data,
    total,
    isLoading,
    isFetching,
    error
  } = useNationalTransfers(t9);
  const isSearching = useSearchPending(debouncedSearch, isFetching);
  let t10;
  if ($[30] !== data || $[31] !== editingRowId) {
    t10 = editingRowId !== null ? data.find(row_0 => row_0.id === editingRowId) ?? null : null;
    $[30] = data;
    $[31] = editingRowId;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const editingRow = t10;
  let t11;
  let t12;
  if ($[33] !== editingRow || $[34] !== editingRowId) {
    t11 = () => {
      if (editingRowId !== null && editingRow === null) {
        setEditingRowId(null);
      }
    };
    t12 = [editingRowId, editingRow];
    $[33] = editingRow;
    $[34] = editingRowId;
    $[35] = t11;
    $[36] = t12;
  } else {
    t11 = $[35];
    t12 = $[36];
  }
  useEffect(t11, t12);
  let t13;
  if ($[37] !== t) {
    t13 = t("paymentsReceived.state.inProgress");
    $[37] = t;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13) {
    t14 = {
      key: "in_progress",
      label: t13
    };
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t) {
    t15 = t("paymentsReceived.state.archived");
    $[41] = t;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = {
      key: "archived",
      label: t15
    };
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t) {
    t17 = t("paymentsReceived.state.all");
    $[45] = t;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t17) {
    t18 = {
      key: "all",
      label: t17
    };
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t14 || $[50] !== t16 || $[51] !== t18) {
    t19 = [t14, t16, t18];
    $[49] = t14;
    $[50] = t16;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  const stateOptions = t19;
  let t20;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = nextState => {
      handleCloseEditor();
      setState(nextState as MissingCardPaymentState);
      setPage(1);
    };
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  const handleStateChange = t20;
  let t21;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = nextDone => {
      handleCloseEditor();
      setDone(nextDone);
      setPage(1);
    };
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  const handleDoneChange = t21;
  let t22;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = (column, direction) => {
      handleCloseEditor();
      setSort({
        field: String(column),
        direction
      });
      setPage(1);
    };
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  const handleSortChange = t22;
  let t23;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = nextRowsPerPage => {
      handleCloseEditor();
      setRowsPerPage(nextRowsPerPage);
      setPage(1);
    };
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  const handleRowsPerPageChange = t23;
  let t24;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = nextPage => {
      handleCloseEditor();
      setPage(nextPage);
    };
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  const handleFetchPage = t24;
  let t25;
  if ($[58] !== t) {
    t25 = t("paymentsReceived.state.inProgress");
    $[58] = t;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== state || $[61] !== stateOptions || $[62] !== t25) {
    t26 = <SegmentedControl ariaLabel={t25} options={stateOptions} value={state} onChange={handleStateChange} />;
    $[60] = state;
    $[61] = stateOptions;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== done) {
    t27 = <PaymentsDoneFilter value={done} onChange={handleDoneChange} />;
    $[64] = done;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== setSearchTerm) {
    t28 = value => setSearchTerm("national_transfers", value);
    $[66] = setSearchTerm;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== search || $[69] !== t28) {
    t29 = <PaymentsSearchInput value={search} onChange={t28} />;
    $[68] = search;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== dealerships || $[72] !== debouncedSearch || $[73] !== done || $[74] !== state) {
    t30 = <ExportButton category="national_transfers" done={done} search={debouncedSearch} state={state} dealerships={dealerships} />;
    $[71] = dealerships;
    $[72] = debouncedSearch;
    $[73] = done;
    $[74] = state;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== t27 || $[77] !== t29 || $[78] !== t30) {
    t31 = <div className="flex flex-wrap items-center gap-2">{t27}{t29}{t30}</div>;
    $[76] = t27;
    $[77] = t29;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t26 || $[81] !== t31) {
    t32 = <div className="flex flex-wrap items-center justify-between gap-2">{t26}{t31}</div>;
    $[80] = t26;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== columns || $[84] !== data || $[85] !== error || $[86] !== isFetching || $[87] !== isLoading || $[88] !== isSearching || $[89] !== page || $[90] !== rowsPerPage || $[91] !== t || $[92] !== total) {
    t33 = error ? <div className="flex h-[24vh] items-center justify-center rounded-xl border border-border dark:border-[#3a3a3a]"><p className="text-sm text-danger">{isForbiddenError(error) ? t("paymentsReceived.states.forbidden") : t("paymentsReceived.states.error")}</p></div> : <DataGrid className="w-full" rows={data} columns={columns} isLoading={isLoading || isSearching} isFetching={isFetching} childrenProps={{
      tableBodyProps: {
        emptyContent: <div className="flex h-[18vh] items-center justify-center"><p className="text-lg text-[#666666] dark:text-[#d1cfcf]">{t("paymentsReceived.nationalTransfers.empty")}</p></div>
      }
    }} onSortChange={handleSortChange} paginationType="paginated" currentPage={page} totalItems={total} rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} defaultRowsPerPage={rowsPerPage} onFetchPage={handleFetchPage} onRowsPerPageChange={handleRowsPerPageChange} />;
    $[83] = columns;
    $[84] = data;
    $[85] = error;
    $[86] = isFetching;
    $[87] = isLoading;
    $[88] = isSearching;
    $[89] = page;
    $[90] = rowsPerPage;
    $[91] = t;
    $[92] = total;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== editingField || $[95] !== editingRow) {
    t34 = editingRow !== null && <NationalTransferRowEditor key={`${editingRow.id}-${editingField}`} row={editingRow} field={editingField} anchorRef={anchorRef} onClose={handleCloseEditor} />;
    $[94] = editingField;
    $[95] = editingRow;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  const t35 = historyRow !== null;
  let t36;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => setHistoryRow(null);
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  const t37 = historyRow?.activities;
  let t38;
  if ($[98] !== t35 || $[99] !== t37) {
    t38 = <HistoryDrawer isOpen={t35} onClose={t36} activities={t37} trackedFields={TRACKED_FIELDS} />;
    $[98] = t35;
    $[99] = t37;
    $[100] = t38;
  } else {
    t38 = $[100];
  }
  let t39;
  if ($[101] !== t32 || $[102] !== t33 || $[103] !== t34 || $[104] !== t38) {
    t39 = <div className="flex flex-col gap-3">{t32}{t33}{t34}{t38}</div>;
    $[101] = t32;
    $[102] = t33;
    $[103] = t34;
    $[104] = t38;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  return t39;
}
function _temp(store) {
  return store.setTerm;
}